<template>
	<label
		:for="inputId"
		class="
			flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10
			text-center transition-colors focus-within:ring-2 focus-within:ring-won-green/40
		"
		:class="dropzoneClasses"
		@dragenter.prevent="dragDepth++"
		@dragover.prevent
		@dragleave.prevent="dragDepth--"
		@drop.prevent="onDrop"
	>
		<input
			:id="inputId"
			type="file"
			accept=".pdf,.docx,.txt"
			class="sr-only"
			:disabled="isUploading"
			@change="onFileChosen"
		/>

		<template v-if="hasFile || (isUploading && fileName)">
			<FileText
				class="mb-2 size-6 text-won-green"
				aria-hidden="true"
			/>
			<p class="text-sm font-medium text-won-blue">
				{{ fileName }}
			</p>
			<p class="mt-2 hidden text-sm text-[#6B7280] md:block">
				<span class="font-medium text-won-green underline underline-offset-2">
					Change resume
				</span>
				or drag and drop to replace
			</p>
			<span
				v-if="!isUploading"
				:class="mobilePickerClasses"
			>
				Choose a different file
			</span>
			<div
				v-if="isUploading"
				class="mt-4 w-full max-w-xs"
			>
				<Progress
					:model-value="progress"
					class="h-1.5"
				/>
			</div>
		</template>

		<template v-else>
			<p class="text-sm text-won-blue">
				<span class="font-semibold text-won-green underline underline-offset-2">
					Upload your resume
				</span>
				<span class="hidden md:inline">
					or drag and drop it here
				</span>
			</p>
			<p class="mt-2 text-xs text-[#6B7280]">
				.pdf, .docx or .txt, up to 10 MB.
			</p>
			<span :class="mobilePickerClasses">
				Choose file
			</span>
		</template>
	</label>
</template>

<script setup>
import { computed, ref } from 'vue';
import { FileText } from 'lucide-vue-next';

import { Progress } from './ui/progress';

/**
 * The resume drop target shared by the onboarding Upload step and the existing-user
 * AutoApply Resume Gate. It only picks the file; the caller uploads it and owns the state.
 */
const props = defineProps({
	inputId: {
		type: String,
		required: true,
	},
	isUploading: {
		type: Boolean,
		default: false,
	},
	progress: {
		type: Number,
		default: 0,
	},
	fileName: {
		type: String,
		default: '',
	},
	hasFile: {
		type: Boolean,
		default: false,
	},
	hasError: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['select']);

const dragDepth = ref(0);
const isDragging = computed(() => dragDepth.value > 0);

const mobilePickerClasses = `
	mt-4 inline-flex h-11 w-full items-center justify-center rounded-lg bg-won-green
	text-sm font-medium text-white md:hidden
`;

const dropzoneClasses = computed(() => {
	const cursor = props.isUploading ? 'cursor-default' : 'cursor-pointer';

	if (props.hasError) {
		return [cursor, 'border-red-300 bg-red-50'];
	}
	if (props.hasFile || (props.isUploading && props.fileName)) {
		return [cursor, 'border-won-green/40 bg-won-green-light/40'];
	}
	if (isDragging.value) {
		return [cursor, 'border-won-green bg-won-green-light/30'];
	}
	return [cursor, 'border-muted-foreground/30 hover:border-won-green/60'];
});

const handleFile = (file) => {
	dragDepth.value = 0;
	if (!file || props.isUploading) {
		return;
	}
	emit('select', file);
};

const onFileChosen = (event) => {
	handleFile(event.target.files?.[0]);
	event.target.value = '';
};

const onDrop = (event) => {
	handleFile(event.dataTransfer?.files?.[0]);
};
</script>
